import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Mail, Trash2 } from "lucide-react";
import { API, Loader, ErrorBox, EmptyState } from "./shared";

export default function ContactsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [actionErr, setActionErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contact`, { withCredentials: true });
      setItems(data || []);
      setErr("");
    } catch (e) {
      console.error("Failed to load contacts", e);
      setErr(e?.response?.data?.detail || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    setActionErr("");
    try {
      await axios.delete(`${API}/contact/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      console.error("Failed to delete contact", e);
      setActionErr("Couldn't delete message — please refresh and try again.");
    }
  };

  if (loading) return <Loader />;
  if (err) return <ErrorBox msg={err} />;
  if (items.length === 0) return <EmptyState icon={Mail} label="No messages yet" />;

  return (
    <div data-testid="admin-contacts-list" className="space-y-3">
      {actionErr && <ErrorBox msg={actionErr} />}
      {items.map((c) => (
        <div
          key={c.id}
          data-testid={`admin-contact-${c.id}`}
          className="rounded-2xl border-2 border-leaf-100 bg-white p-5 transition-colors hover:border-leaf-300"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-display text-xl font-bold text-leaf-900">{c.name}</h3>
                <span className="rounded-full bg-leaf-50 px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-leaf-700">
                  {c.subject || "General"}
                </span>
              </div>
              <p className="mt-1 text-sm text-leaf-900/70">
                <a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a>
                {c.phone && <span> · {c.phone}</span>}
              </p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-leaf-900/85">{c.message}</p>
              <p className="mt-2 text-xs text-leaf-900/50">{new Date(c.created_at).toLocaleString()}</p>
            </div>
            <button
              data-testid={`admin-contact-delete-${c.id}`}
              onClick={() => remove(c.id)}
              aria-label="Delete message"
              className="rounded-full p-2 text-clay-500 hover:bg-clay-500/10"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
